<template>
  <div class="dashboard-page">
    <div :class="[$globalStyle.headerContainer, 'dashboard-header-container']">
      <HeaderActionButton
        v-if="showBackButton"
        :text="$t('back')"
        :iconLink="leftArrowIcon"
        :clickFunction="onBack"
        backBg="#000000"
        frontBg="#676767"
        textColor="#FFFFFF"
        :button-width="'130px'"
      />
      <div :class="[$globalStyle.header, 'dashboard-header']">
        <div :class="[$globalStyle.headerLeft, 'dashboard-header-left']">
          <img :src="saboteurLogo" alt="icon" :class="$globalStyle.headerLogo" />
          <div :class="$globalStyle.headerTitle">{{ $t("saboteur") }} - {{ $t(roleLabel) }}</div>
        </div>
        <div :class="[$globalStyle.headerRight, 'dashboard-header-right']">
          <HeaderLanguageSelector />

          <div v-if="isLoggedIn && user?.firstName" class="dashboard-user-logged">
            <div class="dashboard-user-avatar" @click="toggleUserMenu">
              <div class="dashboard-user-avatar-letter">{{ user.firstName.slice(0, 1).toUpperCase() }}</div>
            </div>

            <div v-if="showUserMenu" class="dashboard-user-menu">
              <div class="dashboard-close-user-menu-btn" @click="toggleUserMenu">
                <img :src="closeIcon" alt="icon" class="dashboard-close-user-menu-icon" />
              </div>
              <div class="dashboard-user-name">{{ user.firstName }}</div>
              <div class="dashboard-role-panel">
                <div class="dashboard-role-panel-label">{{ $t("dashboard-current-role") }}</div>
                <div class="dashboard-role-chip">{{ currentRoleLabel }}</div>
              </div>
              <div v-if="canSwitchRole" class="dashboard-role-panel">
                <div class="dashboard-role-panel-label">{{ $t("dashboard-switch-role") }}</div>
                <div class="dashboard-role-options">
                  <button
                    v-for="option in roleOptions"
                    :key="option.value"
                    type="button"
                    :class="[
                      'dashboard-role-option',
                      { 'dashboard-role-option-active': option.value === activeRole },
                    ]"
                    :disabled="option.value === activeRole"
                    @click="handleRoleSelect(option.value)"
                  >
                    {{ getRoleLabel(option) }}
                  </button>
                </div>
              </div>
              <div class="dashboard-logout-btn">
                <UtilButton
                  :text="$t('logout')"
                  :clickFunction="onLogout"
                  size="sm"
                  backBg="#A50000"
                  frontBg="#F77575"
                  textColor="#FFFFFF"
                  buttonWidth="120px"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="dashboard-body-container">
      <h1 class="dashboard-page-title">{{ $t(pageTitle) }}</h1>
      <div class="dashboard-section">
        <div
          v-for="card in visibleCards"
          :key="card.key || card.title"
          class="dashboard-card"
        >
          <h2 class="dashboard-card-title">{{ $t(card.title) }}</h2>
          <p class="dashboard-card-description">{{ $t(card.description) }}</p>
          <div class="dashboard-card-action">
            <ActionButton
              :text="$t(card.buttonText)"
              :clickFunction="card.clickFunction"
              :backBg="card.backBg"
              :frontBg="card.frontBg"
              :textColor="card.textColor || '#FFFFFF'"
              :buttonWidth="card.buttonWidth || null"
            />
          </div>
        </div>
      </div>
    </div>

    <slot />
  </div>
</template>

<script>
import globalStyles from "@/css/global.module.css";
import SaboteurLogo from "@/assets/saboteur-logo.svg";
import CloseIcon from "@/assets/icons/close-red.svg";
import HeaderActionButton from "@/components/buttons/HeaderActionButton.vue";
import ActionButton from "@/components/buttons/ActionButton.vue";
import HeaderLanguageSelector from "@/components/util/HeaderLanguageSelector.vue";
import UtilButton from "@/components/util/UtilButton.vue";
import LeftArrowIcon from "@/assets/icons/left-arrow.svg";

export default {
  name: "InternalDashboardLayout",
  components: {
    HeaderActionButton,
    ActionButton,
    HeaderLanguageSelector,
    UtilButton,
  },
  props: {
    roleLabel: {
      type: String,
      required: true,
    },
    pageTitle: {
      type: String,
      required: true,
    },
    cards: {
      type: Array,
      default: () => [],
    },
    roleOptions: {
      type: Array,
      default: () => [],
    },
    activeRole: {
      type: String,
      default: "",
    },
    user: {
      type: Object,
      default: null,
    },
    isLoggedIn: {
      type: Boolean,
      default: false,
    },
    showBackButton: {
      type: Boolean,
      default: true,
    },
    onBack: {
      type: Function,
      required: true,
    },
    onLogout: {
      type: Function,
      required: true,
    },
    onRoleChange: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      saboteurLogo: SaboteurLogo,
      closeIcon: CloseIcon,
      leftArrowIcon: LeftArrowIcon,
      showUserMenu: false,
    };
  },
  computed: {
    $globalStyle() {
      return globalStyles;
    },
    visibleCards() {
      return this.cards.filter((card) => card && card.visible !== false);
    },
    canSwitchRole() {
      return Array.isArray(this.roleOptions) && this.roleOptions.length > 1;
    },
    currentRoleLabel() {
      const currentOption = (this.roleOptions || []).find((option) => option.value === this.activeRole);
      if (currentOption) return this.getRoleLabel(currentOption);
      return this.activeRole || "";
    },
  },
  methods: {
    getRoleLabel(option) {
      if (option?.labelKey) return this.$t(option.labelKey);
      return option?.label || option?.value || "";
    },
    handleRoleSelect(nextRole) {
      if (!nextRole || nextRole === this.activeRole) return;
      this.onRoleChange(nextRole);
      this.showUserMenu = false;
    },
    toggleUserMenu() {
      this.showUserMenu = !this.showUserMenu;
    },
  },
};
</script>

<style scoped>
.dashboard-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.dashboard-body-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 24px 12px 32px;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
}

.dashboard-page-title {
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  font-size: 24px;
  line-height: 1.2;
  color: #333;
  margin-bottom: 24px;
  text-align: center;
}

.dashboard-section {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
}

.dashboard-card {
  background: #fff;
  border-radius: 12px;
  padding: 22px 18px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 600px;
  text-align: center;
  box-sizing: border-box;
}

.dashboard-card-title {
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  font-size: 20px;
  line-height: 1.25;
  color: #333;
  margin-bottom: 10px;
}

.dashboard-card-description {
  font-family: "Poppins", sans-serif;
  font-size: 15px;
  color: #666;
  margin-bottom: 18px;
  line-height: 1.5;
}

.dashboard-card-action {
  display: flex;
  justify-content: center;
  width: 100%;
}

.dashboard-user-logged {
  position: relative;
}

.dashboard-user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #3f72af;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  font-weight: 600;
  font-size: 18px;
}

.dashboard-user-avatar-letter {
  color: white;
}

.dashboard-user-menu {
  position: absolute;
  top: 44px;
  right: 0;
  background: white;
  border-radius: 14px;
  box-shadow: 0 18px 48px rgba(17, 45, 78, 0.18);
  border: 1px solid rgba(103, 114, 148, 0.12);
  padding: 16px;
  width: min(340px, calc(100vw - 16px));
  max-width: calc(100vw - 16px);
  z-index: 1000;
  box-sizing: border-box;
}

.dashboard-close-user-menu-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
}

.dashboard-close-user-menu-icon {
  width: 16px;
  height: 16px;
}

.dashboard-user-name {
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  font-size: 18px;
  color: #23395b;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e0e0e0;
}

.dashboard-role-panel {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;
  margin-bottom: 14px;
}

.dashboard-role-panel-label {
  font-family: "Poppins", sans-serif;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #6f6f6f;
}

.dashboard-role-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 34px;
  padding: 0 12px;
  max-width: 100%;
  border-radius: 999px;
  background: linear-gradient(135deg, #e8eff8 0%, #d4e3f7 100%);
  color: #112d4e;
  font-family: "Poppins", sans-serif;
  font-size: 14px;
  font-weight: 600;
  text-align: center;
  white-space: normal;
  overflow-wrap: anywhere;
  line-height: 1.3;
}

.dashboard-role-options {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
}

.dashboard-role-option {
  border: 1px solid rgba(17, 45, 78, 0.16);
  border-radius: 12px;
  min-height: 38px;
  padding: 8px 12px;
  max-width: 100%;
  background: #f8fafc;
  color: #112d4e;
  font-family: "Poppins", sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.18s ease, color 0.18s ease, border-color 0.18s ease, transform 0.18s ease;
  text-align: center;
  white-space: normal;
  overflow-wrap: anywhere;
  line-height: 1.3;
}

.dashboard-role-option:hover:not(:disabled) {
  background: #eef4fb;
  transform: translateY(-1px);
  border-color: rgba(17, 45, 78, 0.28);
}

.dashboard-role-option-active {
  background: #112d4e;
  color: #fff;
  border-color: #112d4e;
  box-shadow: 0 8px 18px rgba(17, 45, 78, 0.18);
}

.dashboard-role-option:disabled {
  cursor: default;
}

@media (max-width: 767px) {
  .dashboard-header-container {
    padding: 10px;
  }

  .dashboard-header {
    min-width: 0;
    padding-left: 10px;
    padding-right: 10px;
  }

  .dashboard-header-left,
  .dashboard-header-right {
    min-width: 0;
  }
}

@media (min-width: 768px) {
  .dashboard-body-container {
    padding: 36px 20px 40px;
  }

  .dashboard-page-title {
    font-size: 32px;
    margin-bottom: 32px;
  }

  .dashboard-section {
    gap: 24px;
  }

  .dashboard-card {
    padding: 30px 28px;
  }

  .dashboard-card-title {
    font-size: 24px;
    margin-bottom: 12px;
  }

  .dashboard-card-description {
    font-size: 16px;
    margin-bottom: 24px;
  }

  .dashboard-user-menu {
    top: 50px;
    padding: 18px;
    width: min(260px, calc(100vw - 24px));
    max-width: calc(100vw - 24px);
  }

  .dashboard-role-panel-label {
    font-size: 12px;
  }

  .dashboard-role-chip {
    min-height: 36px;
    font-size: 15px;
  }

  .dashboard-role-option {
    font-size: 14px;
  }
}

.dashboard-logout-btn {
  display: flex;
  justify-content: center;
  padding-top: 4px;
}
</style>
